import { ChartData, ChartOptions } from "chart.js";

import { ColorConstants } from "../../../../../constants";
import { formatNumber, thousandsFormatterToLower } from "../../../../../utils";

interface UseSpendVsPredictedReturnsProps {
  plannedInvestment: number | undefined;
  predictedReturns: number | undefined;
}

interface UseSpendVsPredictedReturnsReturnData {
  chartData: ChartData<"bar", number[], string>;
  options: ChartOptions<"bar">;
}

export const useSpendVsPredictedReturns = ({
  plannedInvestment,
  predictedReturns,
}: UseSpendVsPredictedReturnsProps): UseSpendVsPredictedReturnsReturnData => {
  const chartData: ChartData<"bar", number[], string> = {
    labels: [""],
    datasets: [
      {
        label: "Planned investment",
        data: [-1 * (plannedInvestment ?? 0)],
        stack: "spend",
        backgroundColor: ColorConstants.VIOLET_500,
        hoverBackgroundColor: ColorConstants.VIOLET_400,
        borderColor: ColorConstants.WHITE,
        borderWidth: 2,
        borderRadius: 8,
        barThickness: 24,
      },
      {
        label: "Predicted returns",
        data: [predictedReturns ?? 0],
        stack: "returns",
        backgroundColor: ColorConstants.GREEN_500,
        hoverBackgroundColor: ColorConstants.GREEN_400,
        borderColor: ColorConstants.WHITE,
        borderWidth: 2,
        borderRadius: 8,
        barThickness: 24,
      },
    ],
  };

  // Calculate tick spacing and rounded max value
  const maxAbsValue = Math.max(plannedInvestment ?? 0, predictedReturns ?? 0);
  const tickStep = maxAbsValue <= 10_000_000 ? 2_000_000 : 5_000_000;
  const roundedMax = Math.ceil(maxAbsValue / tickStep) * tickStep;

  const options: ChartOptions<"bar"> = {
    indexAxis: "y",
    maintainAspectRatio: false,
    devicePixelRatio: 4,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = typeof context.parsed.x === "number" ? context.parsed.x : context.parsed;
            return `${context.dataset.label}: £${formatNumber(Math.abs(Number(value)))}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: -roundedMax,
        max: roundedMax,
        grid: {
          color: (context) => (context.tick.value === 0 ? ColorConstants.INDIGO_100 : undefined),
        },
        border: {
          display: false,
        },
        ticks: {
          stepSize: tickStep,
          callback: (value) =>
            `${(value as number) < 0 ? "-" : ""}£${thousandsFormatterToLower(Math.abs(Number(value)))}`,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
    },
  };

  return {
    chartData,
    options,
  };
};
